import parse from 'html-react-parser';
import Head from 'next/head';
import { FC, useMemo } from 'react';
import { match } from 'ts-pattern';

import { LocalAssets, PagesTheme } from '@/shared/api/assets';

import headerStyle from './headers/Header.module.scss';
import { MarketingHeader } from './headers/MarketingHeader';
import { PartnershipHeader } from './headers/PartnershipHeader';

interface SharedAssetsProps {
  assetsData: LocalAssets;
}

export interface HeaderSettings {
  theme?: PagesTheme;
  partnershipLogo?: string;
  stickyHeader?: boolean;
}

type HeaderProps = HeaderSettings & SharedAssetsProps;

export const Header: FC<HeaderProps> = ({
  assetsData,
  theme = PagesTheme.BUYER,
  partnershipLogo,
  stickyHeader,
}) => {
  const header = assetsData.header;
  const fullHeader = useMemo(
    () => ({
      __html: match(stickyHeader)
        .with(true, () => {
          const headerClassIndex = header.indexOf('c-header__main');
          if (headerClassIndex < 0) return header;
          return [
            header.slice(0, headerClassIndex),
            `${headerStyle['always-shown']} `,
            header.slice(headerClassIndex),
          ].join('');
        })
        .otherwise(() => header),
    }),
    [header, stickyHeader]
  );
  return match(theme)
    .with(PagesTheme.MARKETING, () => (
      <MarketingHeader assetsData={assetsData} />
    ))
    .with(PagesTheme.PARTNERSHIP, () => (
      <PartnershipHeader
        assetsData={assetsData}
        partnershipLogo={partnershipLogo}
        stickyHeader={stickyHeader}
      />
    ))
    .otherwise(() => (
      <div
        id="cw-header-container"
        suppressHydrationWarning
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={fullHeader}
      />
    ));
};

export const HeadTag: FC<SharedAssetsProps> = ({ assetsData }) => {
  const head = assetsData.third_party.gtm;
  return <Head>{parse(head)}</Head>;
};

export const CssTag: FC<SharedAssetsProps> = ({ assetsData }) => {
  const css = assetsData.tags.head + assetsData.tags.css;
  return <>{parse(css)}</>;
};

export const JsTag: FC<SharedAssetsProps> = ({ assetsData }) => (
  <div
    id="cw-js-container"
    suppressHydrationWarning
    // eslint-disable-next-line react/no-danger
    dangerouslySetInnerHTML={{ __html: assetsData.tags.js }}
  />
);
